import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import type {
  CreateOrganisationRequest,
  GetMyOrganisationResponse,
  InviteOrganisationAdminRequest,
  InviteOrganisationAdminResponse,
  InviteOrganisationMemberRequest,
  InviteOrganisationMemberResponse,
  OrganisationAdmin,
  OrganisationAdminsResponse,
  OrganisationCheckoutResponse,
  OrganisationGroup,
  OrganisationInvitation,
  OrganisationMember,
  OrganisationMembersResponse,
  OrganisationResponse,
  OrganisationSeatKind,
  OrganisationSeatPlan,
  RespondToOrganisationInviteResponse,
  SetMembershipGroupsRequest,
} from '@dreamai/types';
import { SEAT_LABELS, apiErrorMessage, formatDate } from './organisationLabels';
import { codedErrorMessage } from './organisationCreditLabels';
import InviteMemberModal, { InviteMemberInput } from './InviteMemberModal';
import OrganisationModal from './OrganisationModal';
import OrganisationRoster, {
  RosterRole,
  RosterRow,
} from './OrganisationRoster';
import OrganisationCreditsPanel from './OrganisationCreditsPanel';
import OrganisationInvitations from './OrganisationInvitations';
import CreateOrganisationModal from './CreateOrganisationModal';
import OrganisationSplash from './OrganisationSplash';
import OrganisationToast from './OrganisationToast';
import OrganisationRemoveMemberModal from './OrganisationRemoveMemberModal';
import SubscriptionGateModal from './SubscriptionGateModal';
import OrganisationTeamAssistants from './OrganisationTeamAssistants';
import OrganisationHandbook from './OrganisationHandbook';
import OrganisationConnectedApps from './OrganisationConnectedApps';
import OrganisationInfoPanel from './OrganisationInfoPanel';
import OrganisationInformationBarriers from './OrganisationInformationBarriers';
import {
  ORGANISATION_ADMIN_TABS,
  ORGANISATION_ID_PARAM,
  ORGANISATION_INVITE_PARAM,
  ORGANISATION_TAB_PARAM,
  OrganisationAdminTab,
  organisationTabPath,
  parseOrganisationTab,
} from './organisationTabs';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as SendIcon } from '../../icons/action-send.svg';
import { ReactComponent as UserPlusIcon } from '../../icons/user-plus.svg';
import './organisation-section.scss';

type Feedback = { type: 'success' | 'error'; message: string } | null;

/**
 * Which organisation the page shows: the caller's seat, else the first one
 * they administer (admins need no seat), unless they picked another one.
 */
function pickOrganisationId(
  me: GetMyOrganisationResponse,
  selected: string | null,
): string | null {
  const ids = [
    ...(me.organisation ? [me.organisation.id] : []),
    ...me.adminOf.map((o) => o.id),
  ];
  if (selected && ids.includes(selected)) return selected;
  return ids[0] ?? null;
}

interface OrganisationSectionProps {
  /**
   * Organisation to show first (must be one the caller sits in or
   * administers, else ignored). Defaults to the caller's own seat.
   */
  initialOrganisationId?: string;
}

const OrganisationSection: FC<OrganisationSectionProps> = ({
  initialOrganisationId,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState<GetMyOrganisationResponse | null>(null);
  const [view, setView] = useState<OrganisationResponse | null>(null);
  const [admins, setAdmins] = useState<OrganisationAdmin[]>([]);
  // The URL wins so a provider callback (`?org=…`) reopens the organisation
  // the admin was connecting rather than the first one in the selector.
  const selectedOrgId = useRef<string | null>(
    searchParams.get(ORGANISATION_ID_PARAM) ?? initialOrganisationId ?? null,
  );
  const [groups, setGroups] = useState<OrganisationGroup[]>([]);
  const [canInviteMembers, setCanInviteMembers] = useState<boolean | null>(
    null,
  );
  const [seatPlan, setSeatPlan] = useState<OrganisationSeatPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [gatePurpose, setGatePurpose] = useState<
    'invite' | 'team_assistant' | null
  >(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  // Seat holder being made an admin; admins are invited by email, which the
  // roster does not carry for seats.
  const [promoting, setPromoting] = useState<RosterRow | null>(null);
  const [removing, setRemoving] = useState<RosterRow | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  // The Groups, Team Assistants, Handbook and Connected apps tabs exist only
  // once their list call has succeeded (the backend answers 404 while the
  // feature is off, 403 for non-admins).
  const [groupsAvailable, setGroupsAvailable] = useState(false);
  const [teamAssistantsAvailable, setTeamAssistantsAvailable] = useState(false);
  const [handbookAvailable, setHandbookAvailable] = useState(false);
  const [connectedAppsAvailable, setConnectedAppsAvailable] = useState(false);
  const [informationBarriersAvailable, setInformationBarriersAvailable] =
    useState(false);

  const tab = parseOrganisationTab(searchParams.get(ORGANISATION_TAB_PARAM));
  const openTab = (next: OrganisationAdminTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'members') params.delete(ORGANISATION_TAB_PARAM);
    else params.set(ORGANISATION_TAB_PARAM, next);
    setSearchParams(params, { replace: true });
  };
  const selectOrganisation = (organisationId: string) => {
    selectedOrgId.current = organisationId;
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(ORGANISATION_ID_PARAM, organisationId);
        return params;
      },
      { replace: true },
    );
  };

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<GetMyOrganisationResponse>(
        '/organisations/me',
        await authHeaders(),
      );
      setMe(data);
      const organisationId = pickOrganisationId(data, selectedOrgId.current);
      if (organisationId) {
        const administers = data.adminOf.some((o) => o.id === organisationId);
        const [full, adminList] = await Promise.all([
          api.get<OrganisationResponse>(
            `/organisations/${organisationId}`,
            await authHeaders(),
          ),
          administers
            ? api.get<OrganisationAdminsResponse>(
                `/organisations/${organisationId}/admins`,
                await authHeaders(),
              )
            : null,
        ]);
        setView(full.data);
        setAdmins(adminList?.data.admins ?? []);
      } else {
        setView(null);
        setAdmins([]);
      }
    } catch (err) {
      console.error('Error loading organisation:', err);
      setFeedback({
        type: 'error',
        message: apiErrorMessage(err, 'Could not load your organisation.'),
      });
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (
      action: () => Promise<string | void>,
      fallback: string,
      key?: string,
    ) => {
      setFeedback(null);
      if (key) setBusyId(key);
      else setSubmitting(true);
      try {
        const message = await action();
        if (message) setFeedback({ type: 'success', message });
        return true;
      } catch (err) {
        console.error(fallback, err);
        setFeedback({
          type: 'error',
          message: codedErrorMessage(err, fallback),
        });
        return false;
      } finally {
        if (key) setBusyId(null);
        else setSubmitting(false);
      }
    },
    [],
  );

  // Refresh once the next pending deletion is due so the deleted row settles,
  // then every 10s while the deletion job is still finishing.
  const nextDeletionAt = (view?.members ?? [])
    .map((m: OrganisationMember) =>
      m.userId && m.deletionScheduledAt
        ? Date.parse(m.deletionScheduledAt)
        : NaN,
    )
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b)[0];
  useEffect(() => {
    if (nextDeletionAt === undefined) return;
    const timer = window.setTimeout(
      () => load(),
      Math.max(nextDeletionAt - Date.now(), 0) + 10_000,
    );
    return () => window.clearTimeout(timer);
  }, [nextDeletionAt, view, load]);

  // Returning from the subscription checkout reopens the invite form, but only
  // once billing confirms the subscription is live.
  const resumeInvite = searchParams.get(ORGANISATION_INVITE_PARAM) === '1';
  useEffect(() => {
    if (!resumeInvite || canInviteMembers === null || !view) return;
    if (canInviteMembers) setInviting(true);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete(ORGANISATION_INVITE_PARAM);
        params.delete('billing_updated');
        return params;
      },
      { replace: true },
    );
  }, [resumeInvite, canInviteMembers, view, setSearchParams]);

  const handleStartSubscription = async () => {
    const organisationId = view?.organisation?.id;
    if (!organisationId) return;
    setGateBusy(true);
    setGateError(null);
    try {
      const { data } = await api.post<OrganisationCheckoutResponse>(
        `/organisations/${organisationId}/billing/subscribe`,
        {
          returnPath:
            gatePurpose === 'team_assistant'
              ? `${organisationTabPath('team-assistants')}&${ORGANISATION_ID_PARAM}=${encodeURIComponent(organisationId)}`
              : `${organisationTabPath('members')}?${ORGANISATION_ID_PARAM}=${encodeURIComponent(organisationId)}&${ORGANISATION_INVITE_PARAM}=1`,
        },
        await authHeaders(),
      );
      window.location.assign(data.checkoutUrl);
    } catch (err) {
      console.error('Could not start the subscription checkout.', err);
      setGateError(
        codedErrorMessage(err, 'Could not start the subscription checkout.'),
      );
      setGateBusy(false);
    }
  };

  // ── No organisation yet ───────────────────────────────────────────────

  /** Resolves to an error message, or null once the organisation exists. */
  const handleCreate = async (
    name: string,
    includeMyAssistant: boolean,
  ): Promise<string | null> => {
    setFeedback(null);
    setSubmitting(true);
    try {
      const body: CreateOrganisationRequest = me?.hasAssistant
        ? { name, includeMyAssistant }
        : { name };
      const { data } = await api.post<OrganisationResponse>(
        '/organisations',
        body,
        await authHeaders(),
      );
      if (!data.success || !data.organisation) throw new Error(data.error);
      const created = data.organisation;
      selectOrganisation(created.id);
      const adminList = await api.get<OrganisationAdminsResponse>(
        `/organisations/${created.id}/admins`,
        await authHeaders(),
      );
      setView(data);
      setAdmins(adminList.data.admins ?? []);
      setMe((prev) =>
        prev
          ? {
              ...prev,
              ...(data.membership
                ? {
                    organisation: created,
                    membership: data.membership,
                    invitations: [],
                    canSeatMyAssistant: false,
                  }
                : {}),
              adminOf: [
                ...prev.adminOf,
                { id: created.id, name: created.name, status: created.status },
              ],
            }
          : prev,
      );
      setCreating(false);
      return null;
    } catch (err) {
      console.error('Could not create the organisation.', err);
      return codedErrorMessage(err, 'Could not create the organisation.');
    } finally {
      setSubmitting(false);
    }
  };

  const respond = async (
    invitation: OrganisationInvitation,
    answer: 'accept' | 'decline',
  ) => {
    await run(
      async () => {
        const { data } = await api.post<RespondToOrganisationInviteResponse>(
          `/organisations/${invitation.organisation.id}/respond`,
          { response: answer },
          await authHeaders(),
        );
        if (!data.success) throw new Error(data.error);
        if (answer === 'accept') {
          await load();
          return `Welcome to ${invitation.organisation.name}.`;
        }
        setMe((prev) =>
          prev
            ? {
                ...prev,
                invitations: prev.invitations.filter(
                  (i) => i.membershipId !== invitation.membershipId,
                ),
              }
            : prev,
        );
        return `Invitation from ${invitation.organisation.name} declined.`;
      },
      'Could not respond to the invitation.',
      invitation.membershipId,
    );
  };

  // ── Organisation admin actions ────────────────────────────────────────

  const organisation = view?.organisation;
  const membership = view?.membership;
  const isAdmin =
    !!organisation &&
    ((me?.adminOf ?? []).some((o) => o.id === organisation.id) ||
      view?.isAdmin === true);
  const switchable = me
    ? [
        ...(me.organisation ? [me.organisation] : []),
        ...me.adminOf.filter((o) => o.id !== me.organisation?.id),
      ]
    : [];

  const applyMembers = (data: OrganisationMembersResponse) => {
    if (!data.success) throw new Error(data.error);
    setView((prev) =>
      prev ? { ...prev, members: data.members ?? prev.members } : prev,
    );
  };

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organisation) return;
    const name = renameValue.trim();
    if (!name || name === organisation.name) {
      setRenaming(false);
      return;
    }
    const ok = await run(async () => {
      const { data } = await api.patch<OrganisationResponse>(
        `/organisations/${organisation.id}`,
        { name },
        await authHeaders(),
      );
      if (!data.success) throw new Error(data.error);
      setView(data);
      setMe((prev) =>
        prev ? { ...prev, organisation: data.organisation } : prev,
      );
      return 'Organisation renamed.';
    }, 'Could not rename the organisation.');
    if (ok) setRenaming(false);
  };

  /** Resolves to an error message, or null once they are an admin. */
  const addAdmin = async (email: string): Promise<string | null> => {
    if (!organisation) return null;
    try {
      const body: InviteOrganisationAdminRequest = { email };
      const { data } = await api.post<InviteOrganisationAdminResponse>(
        `/organisations/${organisation.id}/admins`,
        body,
        await authHeaders(),
      );
      if (!data.success) throw new Error(data.error);
      if (data.admins) setAdmins(data.admins);
      return null;
    } catch (err) {
      console.error('Could not add that admin.', err);
      return codedErrorMessage(err, 'Could not add that admin.');
    }
  };

  const handleInvite = async (
    input: InviteMemberInput,
  ): Promise<string | null> => {
    if (!organisation) return null;
    setFeedback(null);
    setSubmitting(true);
    try {
      const body: InviteOrganisationMemberRequest = input;
      const { data } = await api.post<InviteOrganisationMemberResponse>(
        `/organisations/${organisation.id}/members`,
        body,
        await authHeaders(),
      );
      if (!data.success) throw new Error(data.error);
      setView((prev) =>
        prev
          ? {
              ...prev,
              members: data.members ?? prev.members,
              externalInvites: data.externalInvites ?? prev.externalInvites,
            }
          : prev,
      );
      setInviting(false);
      setFeedback({
        type: 'success',
        message: data.message || 'Invitation sent.',
      });
      return null;
    } catch (err) {
      console.error('Could not send the invitation.', err);
      return codedErrorMessage(err, 'Could not send the invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromote = async () => {
    if (!promoting) return;
    const email = promoteEmail.trim();
    if (!email.includes('@')) {
      setPromoteError('Add the email address they sign in to Dream with');
      return;
    }
    setFeedback(null);
    setSubmitting(true);
    const error = await addAdmin(email);
    setSubmitting(false);
    if (error) {
      setPromoteError(error);
      return;
    }
    setFeedback({
      type: 'success',
      message: `${promoting.name} is now an admin.`,
    });
    setPromoting(null);
  };

  const handleRemoveAdmin = async (admin: OrganisationAdmin) => {
    if (!organisation) return;
    const who = admin.isMe
      ? 'yourself'
      : admin.name || admin.email || 'this admin';
    if (
      admin.status === 'active' &&
      !window.confirm(
        `Remove ${who} as an admin of ${organisation.name}? ${
          admin.isMe
            ? 'You will lose access to these settings immediately.'
            : 'Their seat, if they have one, is not affected.'
        }`,
      )
    ) {
      return;
    }
    await run(
      async () => {
        const { data } = await api.delete<OrganisationAdminsResponse>(
          `/organisations/${organisation.id}/admins/${admin.id}`,
          await authHeaders(),
        );
        if (!data.success) throw new Error(data.error);
        if (admin.isMe) {
          await load();
          return `You no longer administer ${organisation.name}.`;
        }
        if (data.admins) setAdmins(data.admins);
        return admin.status === 'invited'
          ? 'Invitation withdrawn.'
          : `${admin.name || admin.email || 'Admin'} removed.`;
      },
      'Could not remove that admin.',
      admin.id,
    );
  };

  const patchMember = async (
    member: OrganisationMember,
    patch: { seatKind?: OrganisationSeatKind },
    message: string,
  ) => {
    if (!organisation) return;
    await run(
      async () => {
        const { data } = await api.patch<OrganisationMembersResponse>(
          `/organisations/${organisation.id}/members/${member.id}`,
          patch,
          await authHeaders(),
        );
        applyMembers(data);
        return message;
      },
      'Could not update that member.',
      member.id,
    );
  };

  const handleChangeRole = async (row: RosterRow, role: RosterRole) => {
    if (role === row.role) return;
    if (role === 'member') {
      if (row.admin) await handleRemoveAdmin(row.admin);
      return;
    }
    const email = row.invite?.channel === 'email' ? row.invite.address : null;
    if (email) {
      setFeedback(null);
      setBusyId(row.invite!.id);
      const error = await addAdmin(email);
      setBusyId(null);
      setFeedback(
        error
          ? { type: 'error', message: error }
          : { type: 'success', message: `${row.name} will be an admin.` },
      );
      return;
    }
    setPromoteEmail('');
    setPromoteError(null);
    setPromoting(row);
  };

  const handleRemove = async (row: RosterRow) => {
    if (!organisation) return;
    if (!row.member) {
      if (row.admin) await handleRemoveAdmin(row.admin);
      return;
    }
    setRemoving(row);
  };

  const confirmRemove = async () => {
    if (!organisation || !removing?.member) return;
    const { member } = removing;
    setRemoveSubmitting(true);
    const ok = await run(
      async () => {
        const { data } = await api.post<OrganisationMembersResponse>(
          `/organisations/${organisation.id}/members/${member.id}/delete`,
          {},
          await authHeaders(),
        );
        applyMembers(data);
        return `${member.name} will be deleted in 5 minutes. Restore to undo.`;
      },
      'Could not remove that member.',
      member.id,
    );
    setRemoveSubmitting(false);
    if (ok) setRemoving(null);
  };

  const handleChangeGroups = async (
    member: OrganisationMember,
    groupIds: string[],
  ) => {
    if (!organisation) return;
    await run(
      async () => {
        const body: SetMembershipGroupsRequest = { groupIds };
        await api.put(
          `/organisations/${organisation.id}/members/${member.id}/groups`,
          body,
          await authHeaders(),
        );
        const chosen = new Set(groupIds);
        setGroups((prev) =>
          prev.map((g) => {
            const has = g.membershipIds.includes(member.id);
            if (chosen.has(g.id) === has) return g;
            return {
              ...g,
              membershipIds: has
                ? g.membershipIds.filter((id) => id !== member.id)
                : [...g.membershipIds, member.id],
            };
          }),
        );
        // Group plans move plan units and credits; the credits panel refetches
        // whenever the members array changes.
        setView((prev) =>
          prev?.members ? { ...prev, members: [...prev.members] } : prev,
        );
      },
      'Could not change that member’s groups.',
      member.id,
    );
  };

  const handleRestore = async (member: OrganisationMember) => {
    if (!organisation) return;
    await run(
      async () => {
        const { data } = await api.post<OrganisationMembersResponse>(
          `/organisations/${organisation.id}/members/${member.id}/restore`,
          {},
          await authHeaders(),
        );
        applyMembers(data);
        return `${member.name} restored.`;
      },
      'Could not restore that member.',
      member.id,
    );
  };

  const handleReactivate = async (member: OrganisationMember) => {
    if (!organisation) return;
    await run(
      async () => {
        const { data } = await api.post<OrganisationMembersResponse>(
          `/organisations/${organisation.id}/members/${member.id}/reactivate`,
          {},
          await authHeaders(),
        );
        applyMembers(data);
        return `${member.name} reactivated.`;
      },
      'Could not reactivate that member.',
      member.id,
    );
  };

  const handleReinvite = async (member: OrganisationMember) => {
    if (!organisation) return;
    await run(
      async () => {
        const { data } = await api.post<OrganisationMembersResponse>(
          `/organisations/${organisation.id}/members/${member.id}/reinvite`,
          {},
          await authHeaders(),
        );
        applyMembers(data);
        return `Invitation sent to ${member.name} again.`;
      },
      'Could not re-invite that member.',
      member.id,
    );
  };

  const handleUninvite = async (row: RosterRow) => {
    if (!organisation) return;
    const path = row.invite
      ? `invites/${row.invite.id}`
      : row.member
        ? `members/${row.member.id}`
        : null;
    if (!path) return;
    await run(
      async () => {
        const { data } = await api.post<OrganisationResponse>(
          `/organisations/${organisation.id}/${path}/uninvite`,
          {},
          await authHeaders(),
        );
        if (!data.success) throw new Error(data.error);
        setView((prev) =>
          prev
            ? {
                ...prev,
                members: data.members ?? prev.members,
                externalInvites: data.externalInvites ?? prev.externalInvites,
              }
            : prev,
        );
        return `Invitation to ${row.name} withdrawn.`;
      },
      'Could not withdraw that invitation.',
      row.member?.id ?? row.invite?.id,
    );
  };

  // ── Render ────────────────────────────────────────────────────────────

  const dismissFeedback = useCallback(() => setFeedback(null), []);

  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = tabsRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!nav || !active) return;
    const left = active.offsetLeft - nav.offsetLeft;
    const right = left + active.offsetWidth;
    if (left < nav.scrollLeft) nav.scrollLeft = left;
    else if (right > nav.scrollLeft + nav.clientWidth)
      nav.scrollLeft = right - nav.clientWidth;
  }, [
    tab,
    loading,
    isAdmin,
    groupsAvailable,
    teamAssistantsAvailable,
    handbookAvailable,
    connectedAppsAvailable,
  ]);

  const renderFeedback = () =>
    feedback &&
    (feedback.type === 'error' ? (
      <p className='organisation-feedback error' role='alert'>
        {feedback.message}
      </p>
    ) : (
      <OrganisationToast
        key={feedback.message}
        message={feedback.message}
        onDismiss={dismissFeedback}
      />
    ));

  if (loading) {
    return (
      <div className='organisation-section'>
        <div className='organisation-header'>
          <h2 className='section-title'>Organisation</h2>
        </div>
        <p className='organisation-empty'>Loading…</p>
      </div>
    );
  }

  if (!organisation) {
    const invitations = me?.invitations ?? [];
    return (
      <div className='organisation-section'>
        {invitations.length > 0 && (
          <OrganisationInvitations
            invitations={invitations}
            busyId={busyId}
            onRespond={respond}
          />
        )}

        {me && (
          <OrganisationSplash
            paused={creating}
            onCreate={() => setCreating(true)}
          />
        )}

        {creating && me && (
          <CreateOrganisationModal
            canAddMyAssistant={me.hasAssistant && me.canSeatMyAssistant}
            submitting={submitting}
            onCreate={handleCreate}
            onClose={() => setCreating(false)}
          />
        )}

        {!me && invitations.length === 0 && (
          <p className='organisation-empty'>
            You are not part of an organisation.
          </p>
        )}

        {renderFeedback()}
      </div>
    );
  }

  const members = view?.members ?? [];
  const externalInvites = view?.externalInvites ?? [];
  const activeCount = members.filter((m) => m.status === 'active').length;
  const visibleTabs = ORGANISATION_ADMIN_TABS.filter((t) => {
    if (t.id === 'groups') return groupsAvailable;
    if (t.id === 'team-assistants') return teamAssistantsAvailable;
    if (t.id === 'handbook') return handbookAvailable;
    if (t.id === 'connected-apps') return connectedAppsAvailable;
    if (t.id === 'information-barriers') return informationBarriersAvailable;
    return true;
  });
  const activeTab: OrganisationAdminTab =
    isAdmin && visibleTabs.some((t) => t.id === tab) ? tab : 'members';

  return (
    <div className='organisation-section'>
      <div className='organisation-header'>
        <span className='organisation-eyebrow'>Organisation</span>
        {renaming ? (
          <form className='organisation-rename' onSubmit={handleRename}>
            <input
              type='text'
              className='field-input'
              aria-label='Organisation name'
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              disabled={submitting}
              maxLength={120}
              autoFocus
            />
            <button
              type='submit'
              className='btn-primary'
              disabled={submitting || !renameValue.trim()}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type='button'
              className='btn-secondary'
              disabled={submitting}
              onClick={() => setRenaming(false)}
            >
              Cancel
            </button>
          </form>
        ) : (
          <div className='organisation-title-row'>
            <h2 className='section-title'>{organisation.name}</h2>
            {isAdmin && (
              <button
                type='button'
                className='organisation-rename-button'
                aria-label='Rename organisation'
                title='Rename organisation'
                onClick={() => {
                  setRenameValue(organisation.name);
                  setRenaming(true);
                }}
              >
                <EditIcon aria-hidden='true' />
              </button>
            )}
          </div>
        )}
        {!isAdmin && (
          <p className='organisation-subtitle'>
            You are a member
            {membership?.seatKind === 'directory'
              ? ` (${SEAT_LABELS.directory.toLowerCase()})`
              : ''}
            {membership?.joinedAt
              ? ` since ${formatDate(membership.joinedAt)}`
              : ''}
            . Your conversations, files and memories stay private to you.
          </p>
        )}
        {switchable.length > 1 && (
          <label className='organisation-field'>
            <span className='field-label'>Showing</span>
            <select
              className='field-select'
              aria-label='Organisation to show'
              value={organisation.id}
              disabled={submitting}
              onChange={(e) => {
                selectOrganisation(e.target.value);
                setLoading(true);
                load();
              }}
            >
              {switchable.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isAdmin && (
        <nav
          ref={tabsRef}
          className='organisation-tabs'
          aria-label='Organisation settings'
          role='tablist'
        >
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              type='button'
              role='tab'
              aria-selected={activeTab === t.id}
              className={`organisation-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => openTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
      )}

      {isAdmin && <OrganisationInfoPanel tab={activeTab} />}

      {activeTab === 'members' && (
        <section className='organisation-card organisation-members-card'>
          <div className='organisation-members-head'>
            <div className='organisation-members-heading'>
              <h3 className='organisation-card-title'>Members</h3>
              <p className='organisation-card-text'>
                {isAdmin
                  ? 'Invite people to your organisation. You can invite new, and existing Dream secretary users.'
                  : `${activeCount} active ${activeCount === 1 ? 'member' : 'members'} in ${organisation.name}.`}
              </p>
            </div>
            {isAdmin && (
              <button
                type='button'
                className='organisation-invite-button'
                onClick={() => {
                  if (canInviteMembers !== false) setInviting(true);
                  else {
                    setGateError(null);
                    setGatePurpose('invite');
                  }
                }}
              >
                Invite member
                <SendIcon aria-hidden='true' />
              </button>
            )}
          </div>
          <OrganisationRoster
            members={members}
            externalInvites={externalInvites}
            admins={admins}
            isAdmin={isAdmin}
            myMembershipId={membership?.membershipId}
            busyId={busyId}
            onChangeSeat={(member, seatKind) =>
              patchMember(
                member,
                { seatKind },
                `${member.name} moved to ${SEAT_LABELS[seatKind].toLowerCase()} seat.`,
              )
            }
            onChangeRole={handleChangeRole}
            onRemove={handleRemove}
            onRestore={handleRestore}
            onReactivate={handleReactivate}
            onReinvite={handleReinvite}
            onUninvite={handleUninvite}
            groups={groups}
            groupsAvailable={groupsAvailable}
            onChangeGroups={handleChangeGroups}
          />
        </section>
      )}

      {activeTab === 'members' && renderFeedback()}

      {isAdmin && inviting && (
        <InviteMemberModal
          groups={groups}
          groupsAvailable={groupsAvailable}
          seatPlan={seatPlan}
          submitting={submitting}
          onInvite={handleInvite}
          onClose={() => setInviting(false)}
        />
      )}

      <OrganisationRemoveMemberModal
        member={removing?.member ?? null}
        isSubmitting={removeSubmitting}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
      />

      {isAdmin &&
        gatePurpose &&
        (gatePurpose === 'team_assistant' || canInviteMembers === false) && (
          <SubscriptionGateModal
            seatPlan={seatPlan}
            busy={gateBusy}
            error={gateError}
            purpose={gatePurpose}
            onSubscribe={handleStartSubscription}
            onClose={() => setGatePurpose(null)}
          />
        )}

      {isAdmin && promoting && (
        <OrganisationModal
          icon={<UserPlusIcon />}
          title={`Make ${promoting.name} an admin`}
          error={promoteError}
          busy={submitting}
          testId='make-admin-modal'
          onClose={() => setPromoting(null)}
          onSubmit={handlePromote}
          actions={
            <>
              <button
                type='button'
                className='organisation-modal-cancel'
                disabled={submitting}
                onClick={() => setPromoting(null)}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='organisation-modal-primary'
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Make admin'}
              </button>
            </>
          }
        >
          <div className='organisation-modal-panel'>
            <label className='organisation-modal-field'>
              <span className='organisation-modal-label'>
                Email they sign in with
              </span>
              <input
                type='email'
                className='organisation-modal-input'
                value={promoteEmail}
                autoFocus
                disabled={submitting}
                aria-invalid={!!promoteError}
                onChange={(e) => {
                  setPromoteEmail(e.target.value);
                  setPromoteError(null);
                }}
              />
            </label>
            <p className='organisation-modal-hint'>
              Admins can manage and invite users, and change organisation
              settings. If this is the email on their Dream account they become
              an admin straight away.
            </p>
          </div>
        </OrganisationModal>
      )}

      {isAdmin && (
        <OrganisationTeamAssistants
          key={organisation.id}
          organisationId={organisation.id}
          visible={activeTab === 'team-assistants'}
          canAddTeamAssistant={canInviteMembers}
          onSubscriptionRequired={() => {
            setGateError(null);
            setGatePurpose('team_assistant');
          }}
          onAvailabilityChange={setTeamAssistantsAvailable}
        />
      )}

      {isAdmin && (
        <OrganisationHandbook
          key={`handbook-${organisation.id}`}
          organisationId={organisation.id}
          teamAssistantsAvailable={teamAssistantsAvailable}
          visible={activeTab === 'handbook'}
          onAvailabilityChange={setHandbookAvailable}
        />
      )}

      {isAdmin && (
        <OrganisationConnectedApps
          key={`connected-apps-${organisation.id}`}
          organisationId={organisation.id}
          members={members}
          groups={groups}
          groupsAvailable={groupsAvailable}
          teamAssistantsAvailable={teamAssistantsAvailable}
          visible={activeTab === 'connected-apps'}
          onAvailabilityChange={setConnectedAppsAvailable}
        />
      )}

      {isAdmin && (
        <OrganisationInformationBarriers
          key={`information-barriers-${organisation.id}`}
          organisationId={organisation.id}
          members={members}
          teamAssistantsAvailable={teamAssistantsAvailable}
          visible={activeTab === 'information-barriers'}
          onAvailabilityChange={setInformationBarriersAvailable}
        />
      )}

      <OrganisationCreditsPanel
        organisation={organisation}
        membership={membership}
        members={members}
        isAdmin={isAdmin}
        view={activeTab}
        onGroupsChange={setGroups}
        onGroupsAvailabilityChange={setGroupsAvailable}
        onCanInviteMembersChange={setCanInviteMembers}
        onSeatPlanChange={setSeatPlan}
      />
    </div>
  );
};

export default OrganisationSection;
